import {
  detectRegionFromTimezone,
  getActiveRegionFromSearch,
  readStoredLyrraOneRegion,
  type LyrraOnePublicRegion,
} from '@/lib/lyrraone-public-region';
import { countryToLyrraOneRegion, getMarketConfig } from '@/lib/lyrraone-market-config';
import { UI_LANGUAGES, type UiLanguageCode } from '@/lib/ui-languages';

export const VISITOR_COUNTRY_STORAGE_KEY = 'visitor_country_code';

const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'Africa/Casablanca': 'MA',
  'Europe/Paris': 'FR',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'Europe/Lisbon': 'PT',
  'Atlantic/Madeira': 'PT',
  'Atlantic/Azores': 'PT',
  'Europe/Rome': 'IT',
  'Europe/Zurich': 'CH',
  'Europe/Luxembourg': 'LU',
  'Europe/Brussels': 'BE',
  'Africa/Algiers': 'DZ',
  'Africa/Tunis': 'TN',
  'Europe/Istanbul': 'TR',
  'Asia/Istanbul': 'TR',
};

export type UiLanguageEntry = (typeof UI_LANGUAGES)[number];

export function normalizeCountryCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const c = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : null;
}

export function storeVisitorCountryCode(country: string): void {
  if (typeof window === 'undefined') return;
  const code = normalizeCountryCode(country);
  if (!code) return;
  try {
    sessionStorage.setItem(VISITOR_COUNTRY_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

export function readStoredVisitorCountryCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeCountryCode(sessionStorage.getItem(VISITOR_COUNTRY_STORAGE_KEY));
  } catch {
    return null;
  }
}

function detectCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TIMEZONE_TO_COUNTRY[tz]) return TIMEZONE_TO_COUNTRY[tz];
  } catch {
    /* ignore */
  }
  return null;
}

function detectCountryFromNavigator(): string | null {
  if (typeof navigator === 'undefined') return null;
  const tags = [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const tag of tags) {
    const parts = String(tag).split('-');
    if (parts.length < 2) continue;
    const region = parts[parts.length - 1]!.toUpperCase();
    if (COUNTRY_TO_REGION_FROM_CODE(region)) return region;
  }
  const primary = (navigator.language ?? '').split('-')[0]?.toLowerCase();
  const region = (navigator.language ?? '').split('-')[1]?.toUpperCase();
  if (primary === 'fr' && !region) return 'FR';
  return null;
}

function COUNTRY_TO_REGION_FROM_CODE(code: string): string | null {
  const mapped = countryToLyrraOneRegion(code);
  if (!mapped) return null;
  const market = getMarketConfig(mapped);
  return market.countryCode === 'INTL' ? null : market.countryCode;
}

/** Indice pays via région marketing LyrraOne (`?region=`, localStorage, fuseau). */
export function countryFromLyrraOnePublicRegion(): string | null {
  if (typeof window === 'undefined') return null;
  const fromSearch = getActiveRegionFromSearch(window.location.search);
  if (fromSearch) {
    const code = getMarketConfig(fromSearch).countryCode;
    return code === 'INTL' ? null : code;
  }
  const stored = readStoredLyrraOneRegion();
  if (stored) {
    const code = getMarketConfig(stored).countryCode;
    return code === 'INTL' ? null : code;
  }
  const fromTz = detectRegionFromTimezone();
  const code = getMarketConfig(fromTz).countryCode;
  return code === 'INTL' ? null : code;
}

export function shouldPrioritizeAmazighe(
  country: string | null | undefined,
  publicRegion?: LyrraOnePublicRegion | null,
): boolean {
  if (normalizeCountryCode(country) === 'MA') return true;
  if (publicRegion === 'ma') return true;
  return countryFromLyrraOnePublicRegion() === 'MA';
}

/** Indice pays synchrone (session, fuseau, région LyrraOne, région navigateur) — affiné par `/public/region-hint`. */
export function detectVisitorCountryCodeSync(): string | null {
  return (
    readStoredVisitorCountryCode() ??
    detectCountryFromTimezone() ??
    countryFromLyrraOnePublicRegion() ??
    detectCountryFromNavigator()
  );
}

/** Langue UI par défaut selon le pays visiteur (sans préférence utilisateur enregistrée). */
export function defaultLanguageForCountry(country: string | null | undefined): UiLanguageCode {
  const region = countryToLyrraOneRegion(country);
  if (region) return getMarketConfig(region).defaultLanguage;
  return 'en';
}

export function defaultLanguageForVisitorSync(): UiLanguageCode {
  return defaultLanguageForCountry(detectVisitorCountryCodeSync());
}

export function orderUiLanguagesForCountry(
  country: string | null | undefined,
  publicRegion?: LyrraOnePublicRegion | null,
): readonly UiLanguageEntry[] {
  if (!shouldPrioritizeAmazighe(country, publicRegion)) return UI_LANGUAGES;
  const zgh = UI_LANGUAGES.find((l) => l.code === 'zgh');
  if (!zgh) return UI_LANGUAGES;
  return [zgh, ...UI_LANGUAGES.filter((l) => l.code !== 'zgh')];
}

export function orderUiLanguagesForVisitorSync(): readonly UiLanguageEntry[] {
  return orderUiLanguagesForCountry(detectVisitorCountryCodeSync());
}
